import React from "react";

// ─── Types ────────────────────────────────────────────────────────────────────

type TextareaState = "default" | "filled" | "focused" | "error" | "disabled";

export interface TextareaFieldProps {
  label?: string;
  description?: string;
  error?: string;
  required?: boolean;
  placeholder?: string;
  value?: string;
  defaultValue?: string;
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement>;
  onFocus?: React.FocusEventHandler<HTMLTextAreaElement>;
  onBlur?: React.FocusEventHandler<HTMLTextAreaElement>;
  rows?: number;
  disabled?: boolean;
  id?: string;
  name?: string;
  maxLength?: number;
  showCounter?: boolean;
  style?: React.CSSProperties;
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const TEXT_STYLE: React.CSSProperties = {
  fontFamily: "var(--font-family-base)",
  fontSize: "var(--font-size-label-s)",
  fontWeight: "var(--font-weight-medium)",
  lineHeight: "var(--line-height-label-s)",
};

function getBoxStyles(state: TextareaState): React.CSSProperties {
  const base: React.CSSProperties = {
    borderRadius: "var(--radius-s)",
    border: "1px solid transparent",
    boxSizing: "border-box",
    transition: "border-color 120ms ease, background-color 120ms ease",
    overflow: "hidden",
  };
  switch (state) {
    case "focused":
      return { ...base, background: "var(--color-bg-primary)", borderColor: "var(--color-border-brand)" };
    case "filled":
      return { ...base, background: "var(--color-bg-primary)", borderColor: "var(--color-border-primary)" };
    case "error":
      return { ...base, background: "var(--color-bg-primary)", borderColor: "var(--color-border-danger)" };
    case "disabled":
      return { ...base, background: "var(--color-bg-accent)", opacity: 0.6 };
    default:
      return { ...base, background: "var(--color-bg-accent)" };
  }
}

// ─── Icons ────────────────────────────────────────────────────────────────────

const WarningIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
    <path d="M8 2L14.5 13.5H1.5L8 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M8 6.5V9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="8" cy="11.5" r="0.75" fill="currentColor" />
  </svg>
);

// ─── Component ────────────────────────────────────────────────────────────────

export function TextareaField({
  label,
  description,
  error,
  required,
  placeholder,
  value,
  defaultValue,
  onChange,
  onFocus,
  onBlur,
  rows = 3,
  disabled,
  id,
  name,
  maxLength,
  showCounter = false,
  style,
}: TextareaFieldProps) {
  const uidRef = React.useRef(`makula-textarea-${Math.random().toString(36).slice(2)}`);
  const textareaId = id ?? uidRef.current;

  const [focused, setFocused] = React.useState(false);
  const [internalValue, setInternalValue] = React.useState(defaultValue ?? "");

  const isControlled = value !== undefined;
  const currentValue = isControlled ? value : internalValue;

  const state: TextareaState = disabled
    ? "disabled"
    : error
    ? "error"
    : focused
    ? "focused"
    : currentValue
    ? "filled"
    : "default";

  const charCount = currentValue.length;
  const isError = state === "error";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--spacing-s)",
        width: "100%",
        ...style,
      }}
    >
      <style>{`#${CSS.escape(textareaId)}::placeholder { color: var(--color-content-secondary); }`}</style>

      {label && (
        <label
          htmlFor={textareaId}
          style={{
            ...TEXT_STYLE,
            display: "flex",
            gap: "var(--spacing-2xs)",
            alignItems: "center",
            color: "var(--color-content-secondary)",
            opacity: disabled ? 0.6 : 1,
            cursor: disabled ? "not-allowed" : "default",
          }}
        >
          <span>{label}</span>
          {required && <span aria-hidden="true">*</span>}
        </label>
      )}

      <div style={getBoxStyles(state)}>
        <textarea
          id={textareaId}
          name={name}
          rows={rows}
          disabled={disabled}
          placeholder={placeholder}
          maxLength={maxLength}
          value={isControlled ? value : internalValue}
          style={{
            ...TEXT_STYLE,
            display: "block",
            width: "100%",
            padding: "var(--spacing-m)",
            background: "transparent",
            border: "none",
            outline: "none",
            resize: "vertical",
            lineHeight: 1.6,
            color: "var(--color-content-primary)",
            boxSizing: "border-box",
            cursor: disabled ? "not-allowed" : "text",
          }}
          onChange={(e) => {
            if (!isControlled) setInternalValue(e.target.value);
            onChange?.(e);
          }}
          onFocus={(e) => {
            setFocused(true);
            onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocused(false);
            onBlur?.(e);
          }}
        />
      </div>

      {/* Error row */}
      {isError && error && (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "var(--spacing-xs)",
            paddingInline: "var(--spacing-2xs)",
            color: "var(--color-content-danger)",
          }}
        >
          <WarningIcon />
          <div style={{ display: "flex", flex: 1, justifyContent: "space-between", gap: "var(--spacing-s)" }}>
            <span style={{ ...TEXT_STYLE, flex: 1, color: "var(--color-content-danger)" }}>{error}</span>
            {showCounter && maxLength !== undefined && (
              <span style={{ ...TEXT_STYLE, color: "var(--color-content-danger)", textAlign: "right", whiteSpace: "nowrap", flexShrink: 0 }}>
                {charCount}/{maxLength}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Description + counter */}
      {!isError && (description || (showCounter && maxLength !== undefined)) && (
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "var(--spacing-s)" }}>
          {description && (
            <span style={{ ...TEXT_STYLE, flex: 1, color: "var(--color-content-secondary)" }}>{description}</span>
          )}
          {showCounter && maxLength !== undefined && (
            <span style={{ ...TEXT_STYLE, color: "var(--color-content-secondary)", textAlign: "right", whiteSpace: "nowrap", flexShrink: 0 }}>
              {charCount}/{maxLength}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
